import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';

function fmtET(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' });
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [churnRisk, setChurnRisk] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [emailModal, setEmailModal] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const [tradeCalls, setTradeCalls] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [importText, setImportText] = useState('');
  const [patreonGifts, setPatreonGifts] = useState({ total: 0, available: 0, assigned: 0, claimed: 0, recent: [] });
  const [addCodeCount, setAddCodeCount] = useState(100);
  const [addingCodes, setAddingCodes] = useState(false);
  const [newTrade, setNewTrade] = useState({ symbol: '', side: 'long', entry_price: '', leverage: '', notes: '' });

  useEffect(() => { loadData(); loadTradeRoom(); loadPatreonGifts(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, statsRes, healthRes, churnRes] = await Promise.all([
        axios.get(`${API}/api/admin/users`),
        axios.get(`${API}/api/admin/stats`),
        axios.get(`${API}/api/admin/health`),
        axios.get(`${API}/api/admin/churn-risk`)
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
      setHealth(healthRes.data);
      setChurnRisk(churnRes.data.users || []);
    } catch (err) {
      console.error('Admin load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTradeRoom = async () => {
    try {
      const [tradesRes, referralsRes] = await Promise.all([
        axios.get(`${API}/api/trade-room/admin/trades`),
        axios.get(`${API}/api/trade-room/admin/referrals`)
      ]);
      setTradeCalls(tradesRes.data.trades || []);
      setReferrals(referralsRes.data.referrals || []);
    } catch (err) {
      console.error('Trade Room load error:', err);
    }
  };

  const createTrade = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/trade-room/admin/trades`, newTrade);
      setNewTrade({ symbol: '', side: 'long', entry_price: '', leverage: '', notes: '' });
      loadTradeRoom();
      showMessage('Trade published.');
    } catch (err) {
      console.error('Create trade error:', err);
      showMessage('Failed to publish trade.');
    }
  };

  const closeTrade = async (id, exit_price) => {
    if (!exit_price) return;
    const entry = tradeCalls.find(t => t.id === id);
    const pnl_percent = entry ? (((Number(exit_price) - Number(entry.entry_price)) / Number(entry.entry_price)) * 100 * (entry.side === 'short' ? -1 : 1)).toFixed(2) : null;
    const result = pnl_percent == null ? null : Number(pnl_percent) > 0 ? 'win' : Number(pnl_percent) < 0 ? 'loss' : 'breakeven';
    try {
      await axios.put(`${API}/api/trade-room/admin/trades/${id}`, { exit_price, status: 'closed', result, pnl_percent });
      loadTradeRoom();
    } catch (err) { console.error('Close trade error:', err); }
  };

  const deleteTrade = async (id) => {
    try {
      await axios.delete(`${API}/api/trade-room/admin/trades/${id}`);
      loadTradeRoom();
    } catch (err) { console.error('Delete trade error:', err); }
  };

  const loadPatreonGifts = async () => {
    try {
      const res = await axios.get(`${API}/api/trade-room/admin/patreon-gifts`);
      setPatreonGifts(res.data);
    } catch (err) {
      console.error('Patreon gifts load error:', err);
    }
  };

  const addPatreonCodes = async () => {
    setAddingCodes(true);
    try {
      await axios.post(`${API}/api/trade-room/admin/patreon-gifts/add-codes`, {
        count: addCodeCount,
        patreon_link: 'https://www.patreon.com/RipBullWinkle/redeem/27907',
      });
      showMessage(`Added ${addCodeCount} BTCC Patreon codes.`);
      loadPatreonGifts();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to add codes.');
    } finally {
      setAddingCodes(false);
    }
  };

  const resendGiftEmail = async (code) => {
    try {
      await axios.post(`${API}/api/trade-room/admin/patreon-gifts/${code}/resend`);
      showMessage('Reminder email sent.');
      loadPatreonGifts();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to resend.');
    }
  };

  const importReferrals = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await axios.post(`${API}/api/trade-room/admin/import-referrals`, { data: importText });
      setSyncMessage(`Imported ${res.data.imported} referral(s).`);
      setImportText('');
      loadTradeRoom();
    } catch (err) {
      setSyncMessage(err.response?.data?.error || 'Import failed.');
    } finally {
      setSyncing(false);
    }
  };

  const showMessage = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const updateTier = async (userId, tier) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/tier`, { tier });
      setUsers(users.map(u => u.id === userId ? { ...u, subscription_tier: tier } : u));
      showMessage('Plan updated successfully.');
    } catch (err) { console.error('Update tier error:', err); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      showMessage('User deleted.');
    } catch (err) { console.error('Delete user error:', err); }
  };

  const sendEmail = async () => {
    if (!emailSubject || !emailBody) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/admin/send-email`, { userId: emailModal.id, subject: emailSubject, message: emailBody });
      showMessage(`Email sent to ${emailModal.email}`);
      setEmailModal(null);
      setEmailSubject('');
      setEmailBody('');
    } catch (err) { console.error('Send email error:', err); }
    finally { setSendingEmail(false); }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "users.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Export error:", err); }
  };

  const filtered = users.filter(u => {
    const matchSearch = search === '' ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase());
    const matchTier = filterTier === 'all' || u.subscription_tier === filterTier;
    return matchSearch && matchTier;
  });

  const tierColor = (tier) => {
    if (tier === 'elite') return { background: '#FEF3C7', color: '#92400E' };
    if (tier === 'premium') return { background: '#E8F5EF', color: '#1a6b4a' };
    return { background: '#F3F4F6', color: '#6B7280' };
  };

  const freeCount = parseInt(stats.tiers?.find(t => t.subscription_tier === 'free')?.count || 0);
  const premiumCount = parseInt(stats.tiers?.find(t => t.subscription_tier === 'premium')?.count || 0);
  const eliteCount = parseInt(stats.tiers?.find(t => t.subscription_tier === 'elite')?.count || 0);
  const mrr = premiumCount * 27;

  const formatUptime = (seconds) => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  const daysSince = (date) => {
    if (!date) return 'Never';
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  if (loading) return <div style={s.loading}>Loading admin dashboard...</div>;

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.logo}>CryptoFolio <span style={s.accent}>CFO</span> <span style={s.adminBadge}>ADMIN</span></div>
        <div style={s.navRight}>
          <a href="/dashboard" style={s.navLink}>My dashboard</a>
          <button onClick={logout} style={s.logoutBtn}>Sign out</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.titleRow}>
          <h1 style={s.pageTitle}>Admin dashboard</h1>
          <div style={{display:"flex", gap:"8px"}}>
            <button onClick={async () => { await axios.post(`${API}/api/admin/send-daily-summary`); showMessage("Daily summary sent to your email!"); }} style={s.exportBtn}>Send daily summary</button>
            <button onClick={exportCSV} style={s.exportBtn}>Export CSV</button>
          </div>
        </div>
        {message && <div style={s.success}>{message}</div>}

        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total users</div>
            <div style={s.statValue}>{stats.total_users || 0}</div>
            <div style={s.statSub}>+{stats.new_this_month || 0} this month</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Free</div>
            <div style={s.statValue}>{freeCount}</div>
            <div style={s.statSub}>No revenue</div>
          </div>
          <div style={{...s.statCard, background: '#E8F5EF', border: '1px solid rgba(26,107,74,0.15)'}}>
            <div style={s.statLabel}>Premium</div>
            <div style={{...s.statValue, color: '#1a6b4a'}}>{premiumCount}</div>
            <div style={s.statSub}>$27/mo each</div>
          </div>
          <div style={{...s.statCard, background: '#1a6b4a', border: 'none'}}>
            <div style={{...s.statLabel, color: "rgba(255,255,255,0.7)"}}>Real MRR (Stripe)</div>
            <div style={{...s.statValue, color: "#fff"}}>${(stats.stripe?.mrr || 0).toLocaleString()}</div>
            <div style={{...s.statSub, color: 'rgba(255,255,255,0.6)'}}>From active Stripe subscriptions</div>
          </div>
        </div>

        <div style={s.tabs}>
          {["users", "churn", "health", "tradeRoom", "patreonGifts"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{...s.tab, ...(activeTab===tab ? s.tabActive : {})}}>
              {tab === "users" ? `Users (${users.length})` : tab === "churn" ? `Churn risk (${churnRisk.length})` : tab === "health" ? "System health" : tab === "tradeRoom" ? "Trade Room" : "BTCC Patreon Invites"}
            </button>
          ))}
        </div>

        {activeTab === 'users' && (
          <div style={s.section}>
            <div style={s.tableHeader}>
              <h2 style={s.sTitle}>All users ({filtered.length})</h2>
              <div style={s.filters}>
                <input style={s.search} placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
                <select style={s.select} value={filterTier} onChange={e => setFilterTier(e.target.value)}>
                  <option value="all">All plans</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                  <option value="elite">Elite</option>
                </select>
              </div>
            </div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={s.thead}>
                    <th style={s.th}>User</th>
                    <th style={s.th}>Plan</th>
                    <th style={s.th}>Holdings</th>
                    <th style={s.th}>AI used</th>
                    <th style={s.th}>Joined</th>
                    <th style={s.th}>Last login</th>
                    <th style={s.th}>Stripe</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} style={s.tr}>
                      <td style={s.td}>
                        <div style={s.userName}>{u.first_name} {u.last_name}</div>
                        <div style={s.userEmail}>{u.email}</div>
                      </td>
                      <td style={s.td}>
                        <span style={{...s.tierBadge, ...tierColor(u.subscription_tier)}}>
                          {u.subscription_tier}
                        </span>
                      </td>
                      <td style={s.td}>{u.holding_count}</td>
                      <td style={s.td}>{u.ai_questions_used || 0}/mo</td>
                      <td style={s.td}>{new Date(u.created_at).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}</td>
                      <td style={s.td}>
                        <span style={{color: !u.last_login ? '#9b9b9b' : daysSince(u.last_login) === 'Today' ? '#1a6b4a' : '#1a1a1a'}}>
                          {daysSince(u.last_login)}
                        </span>
                      </td>
                      <td style={s.td}>
                        {u.stripe_subscription_id
                          ? <span style={{color:'#1a6b4a', fontSize:'12px'}}>✓ Active</span>
                          : <span style={{color:'#9b9b9b', fontSize:'12px'}}>None</span>}
                      </td>
                      <td style={s.td}>
                        <div style={s.actions}>
                          <select style={s.tierSelect} value={u.subscription_tier} onChange={e => updateTier(u.id, e.target.value)}>
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                                    </select>
                          <button onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/api/admin/impersonate/${u.id}`);
                              const currentToken = localStorage.getItem('token');
                              localStorage.setItem('admin_return_token', currentToken);
                              localStorage.setItem('token', res.data.token);
                              localStorage.setItem('impersonating_user', u.email);
                              window.location.href = '/dashboard';
                            } catch(e) { console.error(e); alert('Failed to impersonate user'); }
                          }} style={{...s.emailBtn, background:"#f0f0ff", color:"#4444cc", borderColor:"rgba(68,68,204,0.3)"}}>View</button>
                          <button onClick={() => setEmailModal(u)} style={s.emailBtn}>Email</button>
                          <button onClick={() => deleteUser(u.id, u.email)} style={s.deleteBtn}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "churn" && (
          <div style={s.section}>
            <div style={s.tableHeader}>
              <h2 style={s.sTitle}>Churn risk — inactive 14+ days ({churnRisk.length})</h2>
              <p style={{fontSize:"13px", color:"#6b6b6b", margin:0}}>Users who havent logged in recently</p>
            </div>
            {churnRisk.length === 0 ? (
              <div style={{padding:"2rem", textAlign:"center", color:"#6b6b6b", fontSize:"14px"}}>No churn risk users right now</div>
            ) : (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr style={s.thead}>
                    <th style={s.th}>User</th>
                    <th style={s.th}>Plan</th>
                    <th style={s.th}>Holdings</th>
                    <th style={s.th}>Joined</th>
                    <th style={s.th}>Last login</th>
                    <th style={s.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {churnRisk.map(u => (
                      <tr key={u.id} style={s.tr}>
                        <td style={s.td}><div style={s.userName}>{u.first_name} {u.last_name}</div><div style={s.userEmail}>{u.email}</div></td>
                        <td style={s.td}><span style={{...s.tierBadge, ...tierColor(u.subscription_tier)}}>{u.subscription_tier}</span></td>
                        <td style={s.td}>{u.holding_count}</td>
                        <td style={s.td}>{new Date(u.created_at).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"})}</td>
                        <td style={{...s.td, color:"#dc2626"}}>{u.last_login ? new Date(u.last_login).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"}) : "Never logged in"}</td>
                        <td style={s.td}><button onClick={() => setEmailModal(u)} style={s.emailBtn}>Send email</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {activeTab === 'health' && (
          <div style={s.healthGrid}>
            <div style={s.healthCard}>
              <div style={s.healthTitle}>Server</div>
              <div style={s.healthRow}><span style={s.healthLabel}>Uptime</span><span style={s.healthValue}>{formatUptime(health.server_uptime || 0)}</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Memory used</span><span style={s.healthValue}>{health.memory_mb || 0} MB</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Database size</span><span style={s.healthValue}>{health.db_size}</span></div>
            </div>
            <div style={s.healthCard}>
              <div style={s.healthTitle}>Coin price history</div>
              <div style={s.healthRow}><span style={s.healthLabel}>Total rows</span><span style={s.healthValue}>{parseInt(health.coin_history?.rows || 0).toLocaleString()}</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Coins tracked</span><span style={s.healthValue}>{health.coin_history?.coins}</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Last updated</span><span style={s.healthValue}>{health.coin_history?.last_updated ? new Date(health.coin_history.last_updated).toLocaleString() : 'N/A'}</span></div>
            </div>
            <div style={s.healthCard}>
              <div style={s.healthTitle}>Portfolio snapshots</div>
              <div style={s.healthRow}><span style={s.healthLabel}>Total snapshots</span><span style={s.healthValue}>{parseInt(health.snapshots?.rows || 0).toLocaleString()}</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Last snapshot</span><span style={s.healthValue}>{health.snapshots?.last_snapshot ? new Date(health.snapshots.last_snapshot).toLocaleString() : 'N/A'}</span></div>
            </div>
            <div style={s.healthCard}>
              <div style={s.healthTitle}>Cron jobs</div>
              <div style={s.healthRow}><span style={s.healthLabel}>Price alerts</span><span style={{...s.healthValue, color:'#1a6b4a'}}>Every 15 min ✓</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Price history</span><span style={{...s.healthValue, color:'#1a6b4a'}}>Every 6 hours ✓</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Daily snapshots</span><span style={{...s.healthValue, color:'#1a6b4a'}}>Midnight UTC ✓</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Storage cleanup</span><span style={{...s.healthValue, color:'#1a6b4a'}}>2am UTC ✓</span></div>
              <div style={s.healthRow}><span style={s.healthLabel}>Monthly reports</span><span style={{...s.healthValue, color:'#1a6b4a'}}>1st of month ✓</span></div>
            </div>
          </div>
        )}
        {activeTab === 'tradeRoom' && (
          <div>
            <div style={s.section}>
              <div style={s.tableHeader}>
                <h2 style={s.sTitle}>BTCC referrals ({referrals.length})</h2>
              </div>
              <div style={{padding:'1.25rem 1.5rem', borderBottom:'1px solid rgba(0,0,0,0.06)'}}>
                <p style={{fontSize:'13px', color:'#6b6b6b', margin:'0 0 8px'}}>
                  BTCC's dashboard blocks automated polling, so import manually: open your BTCC affiliate dashboard,
                  open DevTools → Network, click the <code>customers/queries</code> request, copy the Response, and paste it below.
                </p>
                <textarea
                  style={{...s.search, width:'100%', minHeight:'90px', fontFamily:'monospace', fontSize:'12px', boxSizing:'border-box'}}
                  placeholder='Paste the BTCC JSON response here, e.g. {"code":0,"data":{"records":[...]}}'
                  value={importText}
                  onChange={e => setImportText(e.target.value)}
                />
                <div style={{display:'flex', alignItems:'center', gap:'12px', marginTop:'8px'}}>
                  <button onClick={importReferrals} disabled={syncing || !importText.trim()} style={s.emailBtn}>{syncing ? 'Importing...' : 'Import referrals'}</button>
                  {syncMessage && <span style={{fontSize:'13px', color:'#6b6b6b'}}>{syncMessage}</span>}
                </div>
              </div>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr style={s.thead}>
                    <th style={s.th}>BTCC UID</th>
                    <th style={s.th}>Invite code</th>
                    <th style={s.th}>First deposit</th>
                    <th style={s.th}>Eligible</th>
                    <th style={s.th}>Linked user</th>
                  </tr></thead>
                  <tbody>
                    {referrals.map(r => (
                      <tr key={r.id} style={s.tr}>
                        <td style={s.td}>{r.btcc_uid}</td>
                        <td style={s.td}>{r.invite_code || '—'}</td>
                        <td style={s.td}>{r.first_deposit_amount != null ? `$${r.first_deposit_amount}` : '—'}</td>
                        <td style={s.td}>{r.eligible ? <span style={{color:'#1a6b4a'}}>✓ Yes</span> : <span style={{color:'#9b9b9b'}}>Pending</span>}</td>
                        <td style={s.td}>{r.linked_user_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {referrals.length === 0 && <div style={{padding:"2rem", textAlign:"center", color:"#6b6b6b", fontSize:"14px"}}>No BTCC referrals synced yet.</div>}
              </div>
            </div>

            <div style={s.section}>
              <div style={s.tableHeader}>
                <h2 style={s.sTitle}>Trade calls ({tradeCalls.length})</h2>
              </div>
              <form onSubmit={createTrade} style={{display:'flex', gap:'8px', padding:'1.25rem 1.5rem', borderBottom:'1px solid rgba(0,0,0,0.06)', flexWrap:'wrap'}}>
                <input style={{...s.search, flex:'1 1 100px'}} placeholder="Symbol (e.g. BTC/USD)" value={newTrade.symbol} onChange={e => setNewTrade({...newTrade, symbol: e.target.value})} required />
                <select style={s.select} value={newTrade.side} onChange={e => setNewTrade({...newTrade, side: e.target.value})}>
                  <option value="long">Long</option>
                  <option value="short">Short</option>
                </select>
                <input style={{...s.search, flex:'1 1 120px'}} type="number" step="any" placeholder="Entry price" value={newTrade.entry_price} onChange={e => setNewTrade({...newTrade, entry_price: e.target.value})} required />
                <input style={{...s.search, flex:'1 1 80px'}} placeholder="Leverage (e.g. 100x)" value={newTrade.leverage} onChange={e => setNewTrade({...newTrade, leverage: e.target.value})} />
                <input style={{...s.search, flex:'2 1 200px'}} placeholder="Notes (optional)" value={newTrade.notes} onChange={e => setNewTrade({...newTrade, notes: e.target.value})} />
                <button type="submit" style={s.emailBtn}>Publish trade</button>
              </form>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr style={s.thead}>
                    <th style={s.th}>Symbol</th>
                    <th style={s.th}>Side</th>
                    <th style={s.th}>Entry</th>
                    <th style={s.th}>Exit</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Result</th>
                    <th style={s.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {tradeCalls.map(t => (
                      <tr key={t.id} style={s.tr}>
                        <td style={s.td}>{t.symbol}</td>
                        <td style={s.td}>{t.side}</td>
                        <td style={s.td}>${t.entry_price}</td>
                        <td style={s.td}>{t.exit_price != null ? `$${t.exit_price}` : '—'}</td>
                        <td style={s.td}>{t.status}</td>
                        <td style={s.td}>{t.result ? `${t.result} (${t.pnl_percent}%)` : '—'}</td>
                        <td style={s.td}>
                          <div style={s.actions}>
                            {t.status !== 'closed' && (
                              <button onClick={() => {
                                const exitPrice = window.prompt(`Exit price for ${t.symbol}?`);
                                if (exitPrice) closeTrade(t.id, exitPrice);
                              }} style={s.emailBtn}>Close</button>
                            )}
                            <button onClick={() => deleteTrade(t.id)} style={s.deleteBtn}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {tradeCalls.length === 0 && <div style={{padding:"2rem", textAlign:"center", color:"#6b6b6b", fontSize:"14px"}}>No trades published yet.</div>}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'patreonGifts' && (
          <div style={s.section}>
            <div style={s.statsGrid}>
              <div style={s.statCard}>
                <div style={s.statLabel}>Total codes</div>
                <div style={s.statValue}>{patreonGifts.total}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Available</div>
                <div style={{...s.statValue, color: Number(patreonGifts.available) <= 50 ? '#dc2626' : '#1a1a1a'}}>{patreonGifts.available}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Sent to members</div>
                <div style={s.statValue}>{patreonGifts.assigned}</div>
              </div>
              <div style={{...s.statCard, background: '#1a6b4a', border: 'none'}}>
                <div style={{...s.statLabel, color: "rgba(255,255,255,0.7)"}}>Claimed on Patreon</div>
                <div style={{...s.statValue, color: "#fff"}}>{patreonGifts.claimed}</div>
              </div>
            </div>

            <div style={s.tableHeader}>
              <h2 style={s.sTitle}>Automated BTCC Patreon gifts</h2>
              <p style={{fontSize:"13px", color:"#6b6b6b", margin:0}}>Every Trade Room activation auto-claims a code and emails it — separate pool from the Kalshi codes at invite.xrprightnow.com.</p>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:'8px', padding:'1.25rem 1.5rem', borderBottom:'1px solid rgba(0,0,0,0.06)'}}>
              <input style={{...s.search, width:'120px'}} type="number" min="1" value={addCodeCount} onChange={e => setAddCodeCount(e.target.value)} />
              <button onClick={addPatreonCodes} disabled={addingCodes} style={s.emailBtn}>{addingCodes ? 'Adding...' : 'Add more codes'}</button>
              <span style={{fontSize:'12px', color:'#9b9b9b'}}>You'll get an email alert automatically once available codes drop to 50.</span>
            </div>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead><tr style={s.thead}>
                  <th style={s.th}>Code</th>
                  <th style={s.th}>Member email</th>
                  <th style={s.th}>Assigned</th>
                  <th style={s.th}>Emailed</th>
                  <th style={s.th}>Claimed on Patreon</th>
                  <th style={s.th}></th>
                </tr></thead>
                <tbody>
                  {patreonGifts.recent.map(r => (
                    <tr key={r.code} style={s.tr}>
                      <td style={s.td}><code style={{fontSize:'12px'}}>{r.code}</code></td>
                      <td style={s.td}>{r.email}</td>
                      <td style={s.td}>{fmtET(r.assigned_at)}</td>
                      <td style={s.td}>{r.emailed_at ? <span style={{color:'#1a6b4a'}}>✓ {fmtET(r.emailed_at)}</span> : <span style={{color:'#9b9b9b'}}>Pending</span>}</td>
                      <td style={s.td}>{r.used_at ? <span style={{color:'#1a6b4a'}}>✓ {fmtET(r.used_at)}</span> : <span style={{color:'#9b9b9b'}}>Not yet — remind them</span>}</td>
                      <td style={s.td}>{!r.used_at && <button onClick={() => resendGiftEmail(r.code)} style={s.emailBtn}>Resend</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {patreonGifts.recent.length === 0 && <div style={{padding:"2rem", textAlign:"center", color:"#6b6b6b", fontSize:"14px"}}>No codes sent yet.</div>}
            </div>
          </div>
        )}
      </div>

      {emailModal && (
        <div style={s.modalOverlay} onClick={() => setEmailModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Email {emailModal.first_name}</h3>
            <p style={s.modalDesc}>{emailModal.email}</p>
            <div style={s.field}>
              <label style={s.label}>Subject</label>
              <input style={s.input} value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Subject line..." />
            </div>
            <div style={s.field}>
              <label style={s.label}>Message</label>
              <textarea style={{...s.input, height:'150px', resize:'vertical'}} value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Write your message..." />
            </div>
            <div style={{display:'flex', gap:'8px', marginTop:'1rem'}}>
              <button onClick={sendEmail} style={s.btn} disabled={sendingEmail}>{sendingEmail ? 'Sending...' : 'Send email'}</button>
              <button onClick={() => setEmailModal(null)} style={s.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'#f4f1eb', fontFamily:'system-ui, sans-serif' },
  loading: { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f4f1eb', color:'#6b6b6b', fontFamily:'system-ui, sans-serif' },
  nav: { background:'#1a1a1a', padding:'0 2rem', height:'60px', display:'flex', alignItems:'center', justifyContent:'space-between' },
  logo: { fontFamily:'Georgia, serif', fontSize:'18px', color:'#fff' },
  accent: { color:'#4ade80' },
  adminBadge: { fontSize:'11px', background:'#dc2626', color:'#fff', padding:'2px 8px', borderRadius:'4px', marginLeft:'8px', fontFamily:'system-ui, sans-serif', fontWeight:'500', letterSpacing:'0.06em' },
  navRight: { display:'flex', alignItems:'center', gap:'1.5rem' },
  navLink: { fontSize:'14px', color:'rgba(255,255,255,0.7)', textDecoration:'none' },
  logoutBtn: { fontSize:'13px', color:'rgba(255,255,255,0.7)', background:'transparent', border:'none', cursor:'pointer' },
  content: { maxWidth:'1200px', margin:'0 auto', padding:'2rem' },
  titleRow: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' },
  pageTitle: { fontSize:'24px', fontWeight:'500', color:'#1a1a1a' },
  exportBtn: { padding:'8px 16px', background:'#fff', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', fontSize:'13px', fontWeight:'500', cursor:'pointer', color:'#1a1a1a' },
  success: { background:'#e8f5ef', border:'1px solid rgba(26,107,74,0.2)', color:'#1a6b4a', padding:'10px 14px', borderRadius:'8px', fontSize:'14px', marginBottom:'1rem' },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:'12px', marginBottom:'1.5rem' },
  statCard: { background:'#fff', borderRadius:'12px', padding:'1.25rem', border:'1px solid rgba(0,0,0,0.06)' },
  statLabel: { fontSize:'11px', fontWeight:'500', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' },
  statValue: { fontSize:'28px', fontWeight:'500', color:'#1a1a1a' },
  statSub: { fontSize:'11px', color:'#9b9b9b', marginTop:'3px' },
  tabs: { display:'flex', gap:'4px', marginBottom:'1.5rem', background:'#fff', padding:'4px', borderRadius:'10px', border:'1px solid rgba(0,0,0,0.06)', width:'fit-content' },
  tab: { padding:'8px 16px', borderRadius:'8px', border:'none', background:'transparent', fontSize:'14px', color:'#6b6b6b', cursor:'pointer' },
  tabActive: { background:'#1a1a1a', color:'#fff' },
  section: { background:'#fff', borderRadius:'16px', border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' },
  tableHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.25rem 1.5rem', borderBottom:'1px solid rgba(0,0,0,0.06)', flexWrap:'wrap', gap:'12px' },
  sTitle: { fontSize:'16px', fontWeight:'500', color:'#1a1a1a' },
  filters: { display:'flex', gap:'8px', flexWrap:'wrap' },
  search: { padding:'8px 12px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', fontSize:'13px', outline:'none', width:'220px' },
  select: { padding:'8px 12px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', fontSize:'13px', outline:'none', background:'#fff' },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  thead: { background:'#f9f9f7' },
  th: { padding:'10px 16px', textAlign:'left', fontSize:'11px', fontWeight:'500', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:'1px solid rgba(0,0,0,0.06)' },
  tr: { borderBottom:'1px solid rgba(0,0,0,0.04)' },
  td: { padding:'12px 16px', fontSize:'14px', color:'#1a1a1a', verticalAlign:'middle' },
  userName: { fontWeight:'500', color:'#1a1a1a', marginBottom:'2px' },
  userEmail: { fontSize:'12px', color:'#6b6b6b' },
  tierBadge: { fontSize:'11px', padding:'3px 10px', borderRadius:'99px', fontWeight:'500', textTransform:'capitalize' },
  actions: { display:'flex', gap:'6px', alignItems:'center' },
  tierSelect: { padding:'5px 8px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'6px', fontSize:'12px', background:'#fff', cursor:'pointer' },
  emailBtn: { padding:'5px 10px', background:'#e8f5ef', border:'1px solid rgba(26,107,74,0.2)', color:'#1a6b4a', borderRadius:'6px', fontSize:'12px', cursor:'pointer' },
  deleteBtn: { padding:'5px 10px', background:'transparent', border:'1px solid #fecaca', color:'#dc2626', borderRadius:'6px', fontSize:'12px', cursor:'pointer' },
  healthGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:'16px' },
  healthCard: { background:'#fff', borderRadius:'16px', padding:'1.5rem', border:'1px solid rgba(0,0,0,0.06)' },
  healthTitle: { fontSize:'15px', fontWeight:'500', color:'#1a1a1a', marginBottom:'1rem', paddingBottom:'0.75rem', borderBottom:'1px solid rgba(0,0,0,0.06)' },
  healthRow: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid rgba(0,0,0,0.04)' },
  healthLabel: { fontSize:'13px', color:'#6b6b6b' },
  healthValue: { fontSize:'13px', fontWeight:'500', color:'#1a1a1a' },
  modalOverlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:'16px', padding:'2rem', width:'100%', maxWidth:'480px', margin:'1rem' },
  modalTitle: { fontSize:'18px', fontWeight:'500', color:'#1a1a1a', marginBottom:'4px' },
  modalDesc: { fontSize:'13px', color:'#6b6b6b', marginBottom:'1.5rem' },
  field: { display:'flex', flexDirection:'column', marginBottom:'1rem' },
  label: { fontSize:'12px', fontWeight:'500', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'6px' },
  input: { padding:'10px 14px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', fontSize:'14px', color:'#1a1a1a', outline:'none', fontFamily:'system-ui, sans-serif' },
  btn: { padding:'10px 20px', background:'#1a6b4a', color:'#fff', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'500', cursor:'pointer' },
  cancelBtn: { padding:'10px 20px', background:'transparent', border:'1px solid rgba(0,0,0,0.12)', color:'#6b6b6b', borderRadius:'8px', fontSize:'14px', cursor:'pointer' }
};
